export const ONE_MIN = 60;
export const ONE_HOUR = 60 * ONE_MIN;
export const SIX_HOURS = 6 * ONE_HOUR;
export const ONE_DAY = 24 * ONE_HOUR;
export const ONE_WEEK = 7 * ONE_DAY;
export const ONE_MONTH = 30 * ONE_DAY;
export const THREE_MONTHS = 90 * ONE_DAY;
export const ONE_YEAR = 365 * ONE_DAY;
export const ALL = Infinity;

export const TIME_FRAMES = {
  ONE_HOUR,
  SIX_HOURS,
  ONE_DAY,
  ONE_WEEK,
  ONE_MONTH,
  THREE_MONTHS,
  ONE_YEAR,
  ALL,
};

export const computeTimeFrame = (intervalTime: number, timeFrame?: number) => {
  if (typeof timeFrame !== 'number' || intervalTime < timeFrame) {
    if (intervalTime <= ONE_HOUR) return ONE_HOUR;
    if (intervalTime <= SIX_HOURS) return SIX_HOURS;
    if (intervalTime <= ONE_DAY) return ONE_DAY;
    if (intervalTime <= ONE_WEEK) return ONE_WEEK;
    if (intervalTime <= ONE_MONTH) return ONE_MONTH;
    if (intervalTime <= THREE_MONTHS) return THREE_MONTHS;
    return ONE_YEAR;
  }
  return timeFrame;
};

export type ChartDataItem<T = any> = {
  time: T; // seconds since epoch
  value: number;
};
/**
 * Fill chart data with missing values
 * @param data
 * @param timeFrame
 * @example
 * const data = [
 *   { time: 1672531200, value: 1 },
 *   { time: 1672531260, value: 2 },
 *   { time: 1672531320, value: 3 },
 *   { time: 1672531380, value: 4 },
 *   { time: 1672531440, value: 5 },
 * ];
 * const result = frameFillChartData(data, ONE_HOUR);
 * console.log(result);
 * @returns
 */
export const frameFillChartData = <T extends ChartDataItem>(data: T[], timeFrame?: number): T[] => {
  if (!data || data.length === 0 || isNaN(Number(data[0].time))) return [];
  const now = Math.floor(Date.now() / 1e3);
  const minTime = Number(data[0].time);
  const lastTime = Number(data[data.length - 1].time);
  const maxTime = Math.max(now, lastTime);
  const intervalTime = now - minTime;
  // Determine actual timeframe based on user input or interval
  const actualTimeFrame = computeTimeFrame(intervalTime, timeFrame);
  // Determine step size based on timeframe
  const step = (() => {
    switch (actualTimeFrame) {
      case ONE_HOUR:
        return ONE_MIN;
      case SIX_HOURS:
        return intervalTime >= SIX_HOURS ? 15 * ONE_MIN : 5 * ONE_MIN;
      case ONE_DAY:
        return intervalTime >= ONE_DAY ? ONE_HOUR : 15 * ONE_MIN;
      case ONE_WEEK:
        return intervalTime >= ONE_WEEK ? 4 * ONE_HOUR : ONE_HOUR;
      case ONE_MONTH:
        return intervalTime >= ONE_MONTH ? 12 * ONE_HOUR : 4 * ONE_HOUR;
      case THREE_MONTHS:
        return intervalTime >= THREE_MONTHS ? ONE_DAY : 12 * ONE_HOUR;
      case ONE_YEAR:
      case ALL:
        return intervalTime >= ONE_YEAR ? 30 * ONE_DAY : ONE_DAY;
      default:
        return ONE_MIN;
    }
  })();
  const result: T[] = [];
  const timeData: { [key: number]: T } = {};
  data.forEach((item: any) => {
    const t = Number(item.time);
    timeData[t] = item;
  });
  const rev = data.slice().reverse();
  // Max iterations to prevent infinite loops (limit to ~10000 data points)
  const MAX_ITERATIONS = 10000;
  if (step > 0 && timeData) {
    let lastData = timeData[minTime];
    let i = minTime;
    let iterations = 0;
    for (i; i <= maxTime && iterations < MAX_ITERATIONS; i += step) {
      iterations++;
      if (Object.prototype.hasOwnProperty.call(timeData, i)) lastData = timeData[i];
      else {
        const item = rev.find(
          item => Number(item.time) >= i - step && Number(item.time) < i && lastData.value !== item.value
        );
        if (item) lastData = item;
      }
      result.push({
        ...lastData,
        time: i,
      });
    }
    if (i < lastTime && iterations < MAX_ITERATIONS) {
      result.push({
        ...timeData[lastTime],
        time: lastTime,
      });
    }
  }
  return result;
};

export const tickMarkFormatter = <T extends number>(time: T, timeFrame: number) => {
  const date = new Date(time * 1e3);
  const intervalTime = Math.ceil(Date.now() / 1e3) - time;
  const actualTimeFrame = computeTimeFrame(intervalTime, timeFrame);
  if (actualTimeFrame <= TIME_FRAMES.ONE_DAY) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (actualTimeFrame <= TIME_FRAMES.ONE_MONTH) {
    return `${date.getMonth() + 1}-${date.getDate()}`;
  }
  return date.toLocaleString('en-US', { month: 'short' }); // e.g., "Mar"
};
